/**
 * MfgSettingsService — server-side key-value settings for the mfg-workcenters
 * module (BQC1/BQC3 migration), backed by the single `iSyn2_MfgSettings` list
 * on the Manufacturing site. See docs/bqc-migration/SCHEMA.md §5.
 *
 * Design rules enforced here:
 *  - The valve FAILS CLOSED: a missing row, an unrecognized value, or a
 *    failed read all resolve to "off". Never cached in localStorage — one
 *    server-side truth for every device.
 *  - DS numbers are allocated through the counter row with optimistic
 *    concurrency (ETag If-Match; 412 → re-read and retry). Gaps are
 *    acceptable, duplicates never (SCHEMA §4 protocol).
 */

import { createSharePointClient } from "./spClient";
import { MFG_SITE_URL } from "./mfgSite";
import {
  parseCogmRates, serialiseCogmRates, validateRate, SHIPPED_CARD,
  type ICogmRateCard, type RateProblem,
} from "./cogmRates";
import {
  parseConsumption, serialiseConsumption, validateYards, SHIPPED_CONSUMPTION_CARD,
  YARDS_MESSAGE, type IRmcConsumptionCard, type YardsPerSet,
} from "./rmcConsumption";
import type { Category } from "./categories";

export const MFG_SETTINGS_LIST = "iSyn2_MfgSettings";

/** The rate-card row. One row holds all seven lines — see cogmRates.ts for
 * the format and why it is coded rather than positional. */
export const COGM_RATES_KEY = "cogmRates";

/** The fabric-consumption row — yards per set, by product line. Its own key
 * rather than a field on the rate card: one is what a set COSTS and the other
 * is what a set EATS, they are maintained by different people, and a save to
 * either must never be able to blank the other. See rmcConsumption.ts. */
export const RMC_CONSUMPTION_KEY = "rmcConsumption";

const RATE_MESSAGE: Record<RateProblem, string> = {
  "not-a-number": "is not a number.",
  negative: "cannot be negative — a line that costs nothing is 0, not a minus.",
  "too-large": "is implausibly large; check for a stray digit.",
  "not-whole-centavo": "has more than two decimal places, so it would print rounded and total unrounded.",
};

export type ValvePosition = "off" | "dry-run" | "live-isolated" | "live";
const VALVE_VALUES: ReadonlySet<string> = new Set(["off", "dry-run", "live-isolated", "live"]);

export interface IReasonCode {
  code: string;
  label: string;
}

/** Seed reason codes (DECISIONS §4). The live list is the settings row. */
export const SEED_REASON_CODES: IReasonCode[] = [
  { code: "NOT_AVAILABLE", label: "Not available" },
  { code: "HEALTH_ISSUE", label: "Health issue" },
  { code: "AT_CAPACITY", label: "At capacity" },
  { code: "QUALITY_HOLD", label: "Quality hold" },
  { code: "BUNDLE_REFUSED", label: "Bundle refused" },
  { code: "RUSH_ORDER", label: "Rush order" },
  { code: "OTHER", label: "Other" },
];

function labelFor(code: string): string {
  const c = code.replace(/_/g, " ").toLowerCase();
  return c.charAt(0).toUpperCase() + c.slice(1);
}

/** Weights + spread setting the What-If simulator writes and Smart Assign reads. */
export interface IScoringTuning {
  weights: { skuComp: number; capacity: number; speed: number; quality: number; tenured: number };
  fairShare: number;
}

interface ISettingRow {
  Id: number;
  settingKey: string;
  settingValue: string;
  __metadata?: { etag?: string };
}

const SELECT = "Id,settingKey,settingValue";

export class MfgSettingsService {
  private readonly sp = createSharePointClient(MFG_SITE_URL);
  private entityType: string | null = null;

  private async getEntityType(): Promise<string> {
    if (this.entityType) return this.entityType;
    const res = await this.sp.raw<{ d: { ListItemEntityTypeFullName: string } }>(
      `/_api/web/lists/getbytitle('${MFG_SETTINGS_LIST}')?$select=ListItemEntityTypeFullName`
    );
    this.entityType = res.d.ListItemEntityTypeFullName;
    return this.entityType;
  }

  /** All settings as a map. One list read — call at boot. */
  public async loadAll(): Promise<Map<string, string>> {
    const rows = await this.sp.getListItems<ISettingRow>(MFG_SETTINGS_LIST, { select: SELECT });
    return new Map(rows.map(r => [r.settingKey, r.settingValue]));
  }

  /**
   * Every setting, read ONCE and shared.
   *
   * This list is 60 rows — one page — and a screen asks it a dozen separate
   * questions the moment it mounts: the table style, the KPI tiles, the
   * labels, two tab rows, the valve, the workcenter config. Each used to be
   * its own filtered query with its own CORS preflight, so opening the
   * Control Tower fired roughly two dozen round trips at one small list at the
   * same instant, and SharePoint answered some of them with 429 — a throttle
   * it serves as a redirect to Throttle.htm, which the browser then blocks on
   * CORS, so the whole screen's appearance quietly fell back to defaults.
   *
   * One read, shared. `inflight` is what does the real work: a dozen hooks
   * mounting in the same tick get the SAME promise rather than a dozen
   * requests, whether or not anything is cached yet.
   */
  private cache?: { at: number; rows: Map<string, ISettingRow> };
  private inflight?: Promise<Map<string, ISettingRow>>;
  /**
   * How long a read is reused. Deliberately short: these are live settings
   * somebody may be editing on another machine, and the point here is to
   * collapse one screen's simultaneous questions, not to hold an answer.
   */
  private static readonly CACHE_MS = 10_000;

  private async allRows(fresh = false): Promise<Map<string, ISettingRow>> {
    const now = Date.now();
    if (!fresh && this.cache && now - this.cache.at < MfgSettingsService.CACHE_MS) return this.cache.rows;
    if (!fresh && this.inflight) return this.inflight;

    const run = (async () => {
      const rows = await this.sp.getListItems<ISettingRow>(MFG_SETTINGS_LIST, { select: SELECT });
      const map = new Map<string, ISettingRow>();
      for (const r of rows) if (r.settingKey) map.set(r.settingKey, r);
      this.cache = { at: Date.now(), rows: map };
      return map;
    })();
    this.inflight = run;
    try {
      return await run;
    } finally {
      if (this.inflight === run) this.inflight = undefined;
    }
  }

  /** Drops the shared read, so the next question goes to the server. Called
   * on every write: a screen that just saved must not read back what it
   * replaced. */
  private invalidate(): void {
    this.cache = undefined;
  }

  private async getRow(key: string, fresh = false): Promise<ISettingRow | null> {
    return (await this.allRows(fresh)).get(key) ?? null;
  }

  public async get(key: string): Promise<string | null> {
    return (await this.getRow(key))?.settingValue ?? null;
  }

  /** Upsert by key. Records who changed it (valve-admin audit, SCREENS-BQC1 §6). */
  public async set(key: string, value: string, updatedBy: string): Promise<void> {
    const body = {
      __metadata: { type: await this.getEntityType() },
      Title: key,
      settingKey: key,
      settingValue: value,
      updatedBy,
      updatedAt: new Date().toISOString(),
    };
    // Fresh, not cached: writing against a row id from a stale read is how a
    // setting gets written to whichever row happened to be there before.
    const existing = await this.getRow(key, true);
    if (existing) {
      await this.sp.updateItem(MFG_SETTINGS_LIST, existing.Id, body);
    } else {
      await this.sp.createItem(MFG_SETTINGS_LIST, body);
    }
    this.invalidate();
  }

  /**
   * The migration valve. FAIL CLOSED on every abnormal path: missing row,
   * unknown value, or read error → "off". Callers may distinguish a broken
   * read from a real "off" via the `readFailed` flag (the UI shows
   * "VALVE · UNKNOWN" but behaves as off).
   */
  public async getValve(): Promise<{ valve: ValvePosition; readFailed: boolean }> {
    try {
      // Deliberately uncached. Everything else on a screen can be a few
      // seconds old; the valve decides whether this app may write to the
      // workbook's world at all, and "off" has to take effect the moment it
      // is set, on every device.
      const raw = (await this.getRow("valve", true))?.settingValue ?? null;
      if (raw !== null && VALVE_VALUES.has(raw)) {
        return { valve: raw as ValvePosition, readFailed: false };
      }
      return { valve: "off", readFailed: raw === null };
    } catch {
      return { valve: "off", readFailed: true };
    }
  }

  /**
   * Allocates the next DS number: bumps the `dsCounter` row under optimistic
   * concurrency and returns `DS <n+1>`. On a 412 (another station won the
   * race) it re-reads and retries. If slip creation fails AFTER this returns,
   * the number is burned — never reuse it, never decrement the counter.
   */
  public async allocateDsNumber(updatedBy: string, maxTries = 10): Promise<string> {
    for (let attempt = 0; attempt < maxTries; attempt++) {
      if (attempt > 0) await new Promise(r => setTimeout(r, 100 * attempt));

      const safe = "dsCounter";
      const rows = await this.sp.getListItems<ISettingRow>(MFG_SETTINGS_LIST, {
        select: SELECT,
        filter: `settingKey eq '${safe}'`,
        top: 1,
      });
      const row = rows[0];
      if (!row) {
        // Refuse to invent a seed — provisioning owns it (SCHEMA §5).
        throw new Error("dsCounter setting row is missing — run provisioning before dispatching.");
      }
      const current = parseInt(row.settingValue, 10);
      if (!Number.isFinite(current)) {
        throw new Error(`dsCounter value "${row.settingValue}" is not a number — fix the settings row.`);
      }
      const etag = row.__metadata?.etag ?? "*";
      const next = current + 1;
      try {
        await this.sp.raw(
          `/_api/web/lists/getbytitle('${MFG_SETTINGS_LIST}')/items(${row.Id})`,
          {
            method: "POST",
            headers: { "IF-MATCH": etag, "X-HTTP-Method": "MERGE" },
            body: JSON.stringify({
              __metadata: { type: await this.getEntityType() },
              settingValue: String(next),
              updatedBy,
              updatedAt: new Date().toISOString(),
            }),
          }
        );
        // The counter moved; a cached read still holding the old number would
        // hand the next caller a DS somebody already has.
        this.invalidate();
        return `DS ${next}`;
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        if (msg.includes(" 412 ")) continue; // etag race — retry with fresh read
        throw e;
      }
    }
    throw new Error("allocateDsNumber: retries exhausted (persistent write contention).");
  }

  /**
   * Allocates the next GRN (Goods Receipt Number): bumps the `grnCounter`
   * row under the same optimistic-concurrency protocol as allocateDsNumber
   * — ETag If-Match, 412 → re-read and retry — and returns the plain
   * incremented number as a string (no "DS " prefix; matches the legacy
   * RECEIVING sheet's own column J format, e.g. "1303").
   *
   * One GRN per DISPATCH SLIP (2026-08-18 design conversation with Mikko),
   * not per WIP — every WIP received off the same DS # shares the one
   * number, matching how the legacy sheet already groups a physical
   * delivery's rows. Callers allocate exactly once per slip, the first time
   * ANY of its WIPs is received (see Receiving.tsx's record()) — a second
   * concurrent allocation racing for the same slip is possible if two
   * operators record against two different WIPs of it at once, but the
   * loser's number is simply burned, never reused: the same "gaps
   * acceptable, duplicates never" posture allocateDsNumber's own docstring
   * already commits to.
   */
  public async allocateGrnNumber(updatedBy: string, maxTries = 10): Promise<string> {
    for (let attempt = 0; attempt < maxTries; attempt++) {
      if (attempt > 0) await new Promise(r => setTimeout(r, 100 * attempt));

      const safe = "grnCounter";
      const rows = await this.sp.getListItems<ISettingRow>(MFG_SETTINGS_LIST, {
        select: SELECT,
        filter: `settingKey eq '${safe}'`,
        top: 1,
      });
      const row = rows[0];
      if (!row) {
        // Refuse to invent a seed — provisioning owns it (SCHEMA §5), and
        // the seed here specifically MUST come from the real RECEIVING
        // sheet's own last-used GRN, confirmed with Mikko, before this
        // is ever called against live data — see SEED_SETTINGS's own
        // comment in Isyn2ProvisioningService.ts.
        throw new Error("grnCounter setting row is missing — run provisioning before allocating a GRN.");
      }
      const current = parseInt(row.settingValue, 10);
      if (!Number.isFinite(current)) {
        throw new Error(`grnCounter value "${row.settingValue}" is not a number — fix the settings row.`);
      }
      const etag = row.__metadata?.etag ?? "*";
      const next = current + 1;
      try {
        await this.sp.raw(
          `/_api/web/lists/getbytitle('${MFG_SETTINGS_LIST}')/items(${row.Id})`,
          {
            method: "POST",
            headers: { "IF-MATCH": etag, "X-HTTP-Method": "MERGE" },
            body: JSON.stringify({
              __metadata: { type: await this.getEntityType() },
              settingValue: String(next),
              updatedBy,
              updatedAt: new Date().toISOString(),
            }),
          }
        );
        this.invalidate();
        return String(next);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        if (msg.includes(" 412 ")) continue; // etag race — retry with fresh read
        throw e;
      }
    }
    throw new Error("allocateGrnNumber: retries exhausted (persistent write contention).");
  }

  /**
   * Scoring weights + fair-share, tuned in the What-If simulator and adopted
   * into the real Smart Assign run. Stored as one compact JSON object (~80
   * chars — well inside the 255-char Text limit, SCHEMA §5 lesson).
   * Returns null when unset or unparseable, so callers fall back to the
   * shipped DEFAULT_WEIGHTS rather than to something half-read.
   */
  public async getScoringTuning(): Promise<IScoringTuning | null> {
    let raw: string | null;
    try {
      raw = await this.get("scoringTuning");
    } catch {
      return null;
    }
    if (!raw) return null;
    try {
      const p = JSON.parse(raw) as Record<string, unknown>;
      const n = (v: unknown): number | null =>
        typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : null;
      const w = {
        skuComp: n(p.skuComp), capacity: n(p.capacity), speed: n(p.speed),
        quality: n(p.quality), tenured: n(p.tenured),
      };
      if (Object.values(w).some(v => v === null)) return null;
      const fair = n(p.fairShare);
      return {
        weights: w as { skuComp: number; capacity: number; speed: number; quality: number; tenured: number },
        fairShare: fair === null || fair < 1 ? 1.5 : fair,
      };
    } catch {
      return null;
    }
  }

  public async setScoringTuning(t: IScoringTuning, updatedBy: string): Promise<void> {
    await this.set("scoringTuning", JSON.stringify({ ...t.weights, fairShare: t.fairShare }), updatedBy);
  }

  /**
   * The COGM rate card, or the shipped one.
   *
   * FALLS BACK RATHER THAN FAILING, on purpose: an unreadable settings row
   * must not leave the costing screen unable to price anything. The card it
   * returns says which it is (`stored`), so the screen can state whose rates a
   * figure was built on instead of implying they are current.
   */
  public async getCogmRates(): Promise<ICogmRateCard> {
    let raw: string | null;
    try {
      raw = await this.get(COGM_RATES_KEY);
    } catch {
      return SHIPPED_CARD;
    }
    return parseCogmRates(raw) ?? SHIPPED_CARD;
  }

  /**
   * Saves a rate card. Throws if any line is unusable, BEFORE writing —
   * a half-written card would price real bundles at a number nobody chose.
   */
  public async setCogmRates(
    card: { rates: Record<Category, number>; effective: string },
    updatedBy: string,
  ): Promise<void> {
    for (const [cat, v] of Object.entries(card.rates) as [Category, number][]) {
      const problem = validateRate(v);
      if (problem) throw new Error(`${cat}: ${RATE_MESSAGE[problem]} Nothing was saved.`);
    }
    // serialiseCogmRates throws past 255 rather than truncating — see there.
    await this.set(COGM_RATES_KEY, serialiseCogmRates(card), updatedBy);
  }

  /**
   * The fabric-consumption card, or the empty one.
   *
   * FALLS BACK TO THE SHIPPED CARD RATHER THAN FAILING, like getCogmRates —
   * a settings row that cannot be read must not leave the surge screen unable
   * to render. The card it returns says which it is (`stored`), so a screen
   * can state whose figures a requirement was built on instead of implying
   * they are the ones somebody typed.
   */
  public async getRmcConsumption(): Promise<IRmcConsumptionCard> {
    let raw: string | null;
    try {
      raw = await this.get(RMC_CONSUMPTION_KEY);
    } catch {
      return SHIPPED_CONSUMPTION_CARD;
    }
    return parseConsumption(raw) ?? SHIPPED_CONSUMPTION_CARD;
  }

  /**
   * Saves a consumption card. Throws if any filled-in line is unusable,
   * BEFORE writing — a half-written card would report a fabric requirement
   * built on a number nobody typed.
   */
  public async setRmcConsumption(
    card: { yardsPerSet: YardsPerSet; effective: string },
    updatedBy: string,
  ): Promise<void> {
    for (const [cat, v] of Object.entries(card.yardsPerSet) as [Category, number | undefined][]) {
      if (v === undefined) continue;   // a blank line is unrated, not invalid
      const problem = validateYards(v);
      if (problem) throw new Error(`${cat}: ${YARDS_MESSAGE[problem]} Nothing was saved.`);
    }
    // serialiseConsumption throws past 255 rather than truncating — see there.
    await this.set(RMC_CONSUMPTION_KEY, serialiseConsumption(card), updatedBy);
  }

  /** Reason codes from settings — the reassign UI blocks if unparseable
   * (never a free-text fallback). Stored as a codes-only JSON array (fits
   * SharePoint's 255-char Text limit); labels derived from the code. Also
   * accepts the legacy [{code,label}] shape. */
  public async getReasonCodes(): Promise<IReasonCode[]> {
    const raw = await this.get("reasonCodes");
    if (raw === null) throw new Error("reasonCodes setting is missing — run provisioning.");
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      throw new Error("reasonCodes setting is not valid JSON — fix the settings row.");
    }
    if (Array.isArray(parsed) && parsed.every(x => typeof x === "string")) {
      return (parsed as string[]).map(code => ({ code, label: labelFor(code) }));
    }
    if (Array.isArray(parsed) && parsed.every(x => typeof x?.code === "string" && typeof x?.label === "string")) {
      return parsed as IReasonCode[];
    }
    throw new Error("reasonCodes setting has the wrong shape — expected [\"CODE\",...] or [{code,label}].");
  }
}

export const mfgSettingsService = new MfgSettingsService();
